/**
 * Central language registry.
 *
 * This is the single place that defines which languages the app supports.
 * Adding a new language is a two-step operation:
 *   1. Add an entry to {@link LANGUAGES} below.
 *   2. Add a matching `<code>.json` file under `src/locales/` and register it
 *      in `src/lib/i18n.ts`.
 *
 * Everything else (the language switcher, RTL handling, the `<html dir>` /
 * `<html lang>` attributes) reads from this registry, so no other file needs
 * to know about the set of supported languages or which ones are right-to-left.
 */

export type LanguageDir = 'ltr' | 'rtl';

export interface LanguageDef {
  /** BCP-47 / i18next language code (e.g. `en`, `he`). */
  code: string;
  /** Short label shown in the compact switcher. */
  label: string;
  /** Endonym — the language's own name, shown in fuller language menus. */
  nativeName: string;
  /** Text direction for this language. */
  dir: LanguageDir;
}

export const LANGUAGES: readonly LanguageDef[] = [
  { code: 'en', label: 'EN', nativeName: 'English', dir: 'ltr' },
  { code: 'he', label: 'עב', nativeName: 'עברית', dir: 'rtl' },
  { code: 'fr', label: 'FR', nativeName: 'Français', dir: 'ltr' },
  { code: 'de', label: 'DE', nativeName: 'Deutsch', dir: 'ltr' },
] as const;

export const DEFAULT_LANGUAGE = 'en';

/**
 * The languages with AUTHORED strings, as distinct from the languages this app knows about.
 * `LANGUAGES` above is the registry — fr and de are in it, are offered in the picker as "coming
 * soon", and are rejected by the API — but they have no translations, so applying one would strand
 * the UI in a locale of raw keys. Every "can we actually show this?" check reads from here.
 */
export const RENDERABLE_LANGUAGES: readonly string[] = ['en', 'he'];

/** Whether the app has authored strings for this code — NOT merely whether it is registered. */
export function isRenderableLanguage(code: string | undefined | null): boolean {
  return !!code && RENDERABLE_LANGUAGES.includes(code);
}

const LANGUAGE_BY_CODE = new Map(LANGUAGES.map((l) => [l.code, l]));

/** Look up a language definition, falling back to the default language. */
export function getLanguage(code: string | undefined | null): LanguageDef {
  return (
    (code ? LANGUAGE_BY_CODE.get(code) : undefined) ??
    LANGUAGE_BY_CODE.get(DEFAULT_LANGUAGE)!
  );
}

/** Text direction for a given language code. */
export function dirForLanguage(code: string | undefined | null): LanguageDir {
  return getLanguage(code).dir;
}

